import { Component, signal, WritableSignal, effect } from '@angular/core';
import { DocumentReceptionService } from '../services/document-reception.service';
import { AssignedTaskService } from '../services/assigned-task.service';
import { MatDialog } from '@angular/material/dialog';
import { DialogService } from '@app/lib/services/dialog.service';
import { ETaskScreenName, IAssignedTask, IAssignedTaskEvent } from '../models/assigned-task.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { BreadcrumbComponent } from '../../lib/components/breadcrumb/breadcrumb.component';
import { IBreadcrumb } from '@app/lib/models/breadcrumb.model';
import { MatTabLabel } from '@angular/material/tabs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { CommonModule } from '@angular/common';
import { FeeAssessmentService } from '../services/fee-assessment-service';
import { FeeAssessmentModel } from '../models/fee-assessment.model';
import { IResponse } from '@app/lib/models/response.model';
import { MatTableDataSource } from '@angular/material/table';
import { TAny } from '@app/lib/models/common.type';
import { DialogMessageComponent } from '../dialog-message/dialog-message.component';
import { MatProgressSpinnerModule, } from '@angular/material/progress-spinner';
import { DashboardComponent } from '../dashboard.component';
import { ConfirmationDialogComponent } from '@app/confirmation-dialog/confirmation-dialog.component';

@Component({
  selector: 'app-fee-assessment',
  standalone: true,
  imports: [BreadcrumbComponent,ReactiveFormsModule,FormsModule,
      MatFormFieldModule,
      MatInputModule,
      MatIconModule,
      MatSelectModule,
      MatButtonModule,MatAutocompleteModule,CommonModule,MatProgressSpinnerModule],
  templateUrl: './fee-assessment.component.html',
  styleUrls: ['./fee-assessment.component.scss'],
})
export class FeeAssessmentComponent {
  // Signals for Breadcrumbs and Task
  public breadcrumbs: WritableSignal<IBreadcrumb[]> = signal([
    { label: 'Case' },
    { label: 'Fee Assessment' },
  ]);
  public task: WritableSignal<IAssignedTask | null> = signal(null);
  feeAssessmentData:FeeAssessmentModel|null=null;
  public feecaseData: WritableSignal<FeeAssessmentModel|null> = signal(null);
  public feecaseData123: MatTableDataSource<FeeAssessmentModel> = new MatTableDataSource();
  public isEditing: WritableSignal<boolean> = signal(false); // Default is Read-Only
  public isLoading: WritableSignal<boolean> = signal(false); // Loader State

  // Loading state
 


  public feeForm: FormGroup = new FormGroup({
    feeAmount: new FormControl({ value: '', disabled: true }, [Validators.required,this.nonZeroValidator]),
    pages: new FormControl({ value: '', disabled: true }, [Validators.required,this.nonZeroValidator]),
    remarks: new FormControl({ value: '', disabled: true }),
     governmentParty: new FormControl(false),
  });

  // Form Group
  public searchFormGrp: FormGroup = new FormGroup({
    caseNumber: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    documentType: new FormControl(''),
  });
  
  constructor(
    private service: FeeAssessmentService,
    private assignTaskService: AssignedTaskService,
    private dialogService: DialogService,
    private dialog: MatDialog,
    private dashboard: DashboardComponent
  ) {
    console.log('🚀 FeeAssessmentComponent Constructor Loaded');
  
    this.assignTaskService.assignTask$
      .pipe(
        takeUntilDestroyed(),
        filter((event) => event.name === ETaskScreenName.FEEASSESSMENT)
      )
      .subscribe({
        next: (event: IAssignedTaskEvent) => {
          console.log('🎯 Task Assigned:', event.task);
          this.task.set(event.task);

          if (event.task?.docId) {
            console.log('📌 Task has docId:', event.task.docId);
            this.searchFormGrp.patchValue({ caseNumber: event.task.docId });
            this.searchHandler(event.task.docId);
          }
        },
      });

    // ✅ Effect to ensure feecaseData updates the MatTableDataSource safely
    effect(() => {
      const feeData = this.feecaseData();
      this.feecaseData123.data = feeData ? [feeData] : []; // ✅ Ensures safe assignment
    });
  }
  
  ngOnInit(): void {
    console.log('🚀 FeeAssessmentComponent Initialized (ngOnInit)');
    
    // ✅ Ensure the task exists when component loads
    const assignedTask = this.task();
    if (assignedTask) {
      console.log('📌 Task Exists in ngOnInit:', assignedTask);
    }
    if (assignedTask && assignedTask.docId) {
      console.log('✅ ngOnInit Calling Search for:', assignedTask.docId);
      this.searchFormGrp.patchValue({ caseNumber: assignedTask.docId });
      this.searchHandler(assignedTask.docId);
    }

    // Remarks required only for Government Party
  this.feeForm.get('governmentParty')?.valueChanges.subscribe((isGovt) => {
    const remarksControl = this.feeForm.get('remarks');

    if (isGovt) {
      remarksControl?.setValidators([Validators.required]);
    } else {
      remarksControl?.clearValidators();
    }

    remarksControl?.updateValueAndValidity();
  });
  }
  

  // Search handler for form submission
  searchHandler(caseNumber?: string): void {
    this.isEditing.set(false); // Disable edit mode
    this.feeForm.get('feeAmount')?.disable();
    this.feeForm.get('pages')?.disable();
    this.feeForm.get('remarks')?.disable();
    if (!caseNumber && this.searchFormGrp.invalid) return;

    const searchValue = caseNumber || this.searchFormGrp.get('caseNumber')?.value;
    this.isLoading.set(true);

    this.service.getfeeAssessment({ "docId": searchValue }).subscribe({
      next: (response: IResponse<FeeAssessmentModel>) => {
        if (response?.data) {
          // ✅ Set data if present
          this.feecaseData.set(response.data);
          this.searchFormGrp.patchValue({ caseNumber: response.data.docId || '' });
          this.updateFormValues();
        } else {
          // ❌ Show dialog if response is null
          this.dialogService.openDialog1( {
            data: {
              title: "Error",
              message: "No Privilege to access the data (or) No Data Exists",
              icon: 'error', // ✅ Default icon if not provided
            }, disableClose: true,           // Prevents closing on outside click or ESC
  hasBackdrop: true,            // Shows backdrop to block interaction
  backdropClass: 'dialog-backdrop', // Optional: custom styling
          });
          this.feecaseData.set(null);
        }
        
        this.isLoading.set(false); 
      },
      error: () => {
        // ❌ Handle error case
        this.feecaseData.set(null);
        this.isLoading.set(false); 
        this.dialogService.openDialog1( {
          data: {
            title: "Error",
            message: "No Privilege to access the data (or) No Data Exists",
            icon: 'error',
          }, disableClose: true,           // Prevents closing on outside click or ESC
  hasBackdrop: true,            // Shows backdrop to block interaction
  backdropClass: 'dialog-backdrop', // Optional: custom styling
        });
      },
    });
}

  toggleEdit(): void {
   if(this.feecaseData()?.governmentParty){
    this.dialogService.openDialog1( {
      data: {
        title: "Information",
        message: "Party Type is Government, Fee is exempted",
        icon: 'info', // ✅ Default icon if not provided
      }, disableClose: true,           // Prevents closing on outside click or ESC
  hasBackdrop: true,            // Shows backdrop to block interaction
  backdropClass: 'dialog-backdrop', // Optional: custom styling
    });
   }
    this.isEditing.set(!this.isEditing());

    // Enable or disable fields based on the toggle
    if (this.isEditing()) {
      this.feeForm.get('feeAmount')?.enable();
      this.feeForm.get('pages')?.enable();
      this.feeForm.get('remarks')?.enable();
    } else {
      this.feeForm.get('feeAmount')?.disable();
      this.feeForm.get('pages')?.disable();
      this.feeForm.get('remarks')?.disable();
    }
  }
  resetHandler(): void {
    this.searchFormGrp.reset();
    this.feecaseData.set(null);
    this.feecaseData123.data = [];
    this.isEditing.set(false);// ✅ Reset table data safely
  }

  // Document type change handler
  onDocumentTypeChange(selectedValue: any): void {
    console.log('Selected Document Type:', selectedValue);
  }

  // Dummy methods for autocomplete and data source (adjust as per your implementation)
  caseList(): any[] {
    return [];
  }

  caseListLoader(): boolean {
    return false; // Adjust this logic to match your loader state
  }

  caseDisplayFn(item: any): string {
    return item ? `${item.caseNumber} - ${item.caseCatagory} - ${item.division}` : '';
  }

  nonZeroValidator(control: AbstractControl): { [key: string]: any } | null {
    const value = control.value;
    if (value !== null && value !== undefined && value.toString().startsWith('0')) {
      return { nonZero: true };
    }
    return null;
  }
  
  private updateFormValues(): void {
    const feeData = this.feecaseData();
    if (feeData) {
  
    // 2. Check if feeAmount is explicitly 0 (handles both number 0 and string '0' / '0.0')
    const incomingFee = Number(feeData.feeAmount);
    const finalFeeAmount = incomingFee === 0 ? 100 : (incomingFee ?? 0);

    this.feeForm.patchValue({
      feeAmount: finalFeeAmount,
      pages: feeData.pages ?? 1,
      remarks: feeData.remarks ?? '',
      governmentParty: feeData.governmentParty ?? false,
    });
  }else {
      this.feeForm.reset();
    }
  }

  getconfirmation(buttontype: string): void {
  // 1. Stop execution if form is invalid
  if (!this.feeForm.valid) {
    return; 
  }

  const currentFeeAmount = this.feeForm.value.feeAmount;
  const originalFeeAmount = this.feecaseData()?.feeAmount;

  // 2. Check for zero or invalid amounts
  if (!currentFeeAmount || currentFeeAmount <= 0) {
    this.dialogService.openDialog1({
      data: {
        title: "Info",
        message: "Please enter a valid Amount",
        icon: 'info',
      }, 
      disableClose: true,
      hasBackdrop: true,
      backdropClass: 'dialog-backdrop',
    });
    return;
  }

  // 3. Check if any modifications were actually made
  if (originalFeeAmount === currentFeeAmount) {
    this.dialogService.openDialog1({
      data: {
        title: "Info",
        message: "No modifications to Save",
        icon: 'info',
      }, 
      disableClose: true,
      hasBackdrop: true,
      backdropClass: 'dialog-backdrop',
    });
    return;
  }

  // 4. Open confirmation dialog
  const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
    data: {
      title: 'Confirm Action',
      message: "Do you want to Save?",
    }, 
    disableClose: true,           
    hasBackdrop: true,            
    backdropClass: 'dialog-backdrop', 
  });

  dialogRef.afterClosed().subscribe((result) => {
    if (result && buttontype === 'Save') {
      this._onsave();
    } else {
      console.log('Cancelled!');
    }
  });
}

public _onsave(): void { 
  this.isLoading.set(true); 

  const caseData = this.feecaseData();
  const reqBody: TAny = {
    docId: caseData?.docId,
    taskId: caseData?.taskId,
    id: caseData?.id,
    governmentParty: caseData?.governmentParty,
    feeAmount: this.feeForm.value.feeAmount,
    pages: this.feeForm.value.pages,
    remarks: caseData?.remarks
  };

  this.service.save(reqBody).subscribe({
    next: (res: IResponse<TAny> | null) => {
      if (res?.success) {
        this.dialogService.openDialog1({
          data: {
            title: "Success",
            message: res.success,
            icon: 'check',
          }, 
          disableClose: true,
          hasBackdrop: true,
          backdropClass: 'dialog-backdrop',
        });

        this.searchFormGrp.get('caseNumber')?.patchValue(caseData?.docId);
        this.searchHandler();
      }
    },
    error: (err) => {
      console.error('Error saving case data:', err);
    },
    complete: () => {
      // ✅ Runs whether the request succeeds or fails
      this.dashboard.refreshSidebarTasks();
      this.cancelEdit();
      this.isLoading.set(false); 
    }
  });
}
  cancelEdit(): void {
    this.isEditing.set(false); // Disable edit mode
  
    this.feeForm.patchValue({
      feeAmount: this.feecaseData()?.feeAmount || 0,
      pages: this.feecaseData()?.pages || 1,
      remarks: this.feecaseData()?.remarks || '',
    });
  
    this.feeForm.get('feeAmount')?.disable();
    this.feeForm.get('pages')?.disable();
    this.feeForm.get('remarks')?.disable();
  }
  onPrint():void{
    if(this.feecaseData()!=null){
      this.isLoading.set(true); 
      const reqData:any=
        {"docId":this.feecaseData()?.docId,"id":this.feecaseData()?.id,"governmentParty":this.feecaseData()?.governmentParty,"feeAmount":this.feecaseData()?.feeAmount,"pages":this.feecaseData()?.pages,"remarks":this.feecaseData()?.remarks||''}
  
        console.log(`ReqData : ${reqData}`);
        this.service.getPrint(reqData).subscribe({
          next: (blob) => {
            if (blob) {
              console.log('✅ Print file received successfully');
              
              // Fix: Correctly open PDF in a new tab
              const blobUrl = window.URL.createObjectURL(blob);
              const newTab = window.open(blobUrl, '_blank');
              
              if (!newTab) {
                console.warn("⚠️ Popup blocked. Please allow pop-ups for this site.");
                this.dialogService.openDialog1( {
                  data: {
                    title: "Warning",
                    message: "Popup blocked! Please allow pop-ups for this site.",
                    icon: 'warning',
                  }, disableClose: true,           // Prevents closing on outside click or ESC
  hasBackdrop: true,            // Shows backdrop to block interaction
  backdropClass: 'dialog-backdrop', // Optional: custom styling
                });
              }
    
              // Fix: Revoke the object URL after some time to free memory
              setTimeout(() => window.URL.revokeObjectURL(blobUrl), 5000);
            } else {
              console.error("❌ Received empty response while printing.");
              this.dialogService.openDialog1( {
                data: {
                  title: "Error",
                  message: "Failed to generate print file. Received empty response.",
                  icon: 'error',
                }, disableClose: true,           // Prevents closing on outside click or ESC
  hasBackdrop: true,            // Shows backdrop to block interaction
  backdropClass: 'dialog-backdrop', // Optional: custom styling
              });
            }
            this.isLoading.set(false); 
          },
          error: (err) => {
            console.error("❌ Error while printing:", err);
            this.dialogService.openDialog1( {
              data: {
                title: "Information",
                message: "Failed to generate print file.",
                icon: "info",
              }, disableClose: true,           // Prevents closing on outside click or ESC
  hasBackdrop: true,            // Shows backdrop to block interaction
  backdropClass: 'dialog-backdrop', // Optional: custom styling
            });
            this.isLoading.set(false); 
          },
        });
      }
    }}
